import 'regenerator-runtime/runtime';
import React, { useRef, useEffect, useState } from 'react';
import { GameEngine } from 'react-game-engine';
import { Button } from '../ui/button';
import { ArrowLeft, ArrowRight, ArrowUp, ArrowDown, RotateCcw, Volume2, VolumeX, Sword, Heart, Gem } from 'lucide-react';

// Game constants
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;
const PLAYER_SPEED = 3;
const TILE_SIZE = 32;
const WORLD_WIDTH = 25;
const WORLD_HEIGHT = 19;

// Simple sound manager
class SoundManager {
  private enabled = true;
  private lastPlayTime: { [key: string]: number } = {};

  play(soundName: string) {
    if (!this.enabled) return;
    
    const now = Date.now();
    if (this.lastPlayTime[soundName] && now - this.lastPlayTime[soundName] < 100) {
      return;
    }
    this.lastPlayTime[soundName] = now;

    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      const frequencies: { [key: string]: number } = {
        sword: 330,
        collect: 440,
        hurt: 150,
        enemy_hit: 220,
        pickup: 523,
        puzzle: 294
      };
      
      oscillator.frequency.setValueAtTime(frequencies[soundName] || 200, audioContext.currentTime);
      oscillator.type = soundName === 'collect' ? 'square' : 'sine';
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
      
      setTimeout(() => audioContext.close(), 200);
    } catch (error) {
      // Silent fail
    }
  }

  toggle() {
    this.enabled = !this.enabled;
    return this.enabled;
  }

  isEnabled() {
    return this.enabled;
  }
}

// Game entities
interface Entity {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  type: string;
}

interface Player extends Entity {
  health: number;
  maxHealth: number;
  direction: 'up' | 'down' | 'left' | 'right';
  isMoving: boolean;
  isAttacking: boolean;
  attackCooldown: number;
  invulnerable: number;
  rubies: number;
  fairies: number;
  hasKey: boolean;
  currentScreen: number;
}

interface Enemy extends Entity {
  health: number;
  direction: 'up' | 'down' | 'left' | 'right';
  speed: number;
  lastDirectionChange: number;
  patrol?: { startX: number; endX: number; startY: number; endY: number };
  aggressive: boolean;
  attackCooldown: number;
}

interface Collectible extends Entity {
  collectibleType: 'ruby' | 'fairy' | 'heart' | 'key';
  value: number;
  collected: boolean;
}

interface Obstacle extends Entity {
  obstacleType: 'tree' | 'rock' | 'water' | 'wall' | 'door' | 'block';
  canPush?: boolean;
  needsKey?: boolean;
  isOpen?: boolean;
}

interface Projectile extends Entity {
  direction: 'up' | 'down' | 'left' | 'right';
  speed: number;
  damage: number;
  lifetime: number;
}

interface NPC extends Entity {
  dialogue: string[];
  currentDialogue: number;
  hasSpoken: boolean;
}

interface GameState {
  player: Player;
  enemies: Enemy[];
  collectibles: Collectible[];
  obstacles: Obstacle[];
  projectiles: Projectile[];
  npcs: NPC[];
  camera: { x: number; y: number };
  keysPressed: { [key: string]: boolean };
  gameStarted: boolean;
  gameWon: boolean;
  gameOver: boolean;
  currentScreen: number;
  showDialogue: boolean;
  currentDialogue: string[];
  dialogueIndex: number;
  screenTransition: boolean;
  worldData: any;
}

// World generation
const generateWorld = () => {
  const screens = [];
  
  // Screen 0: Starting forest area
  screens.push({
    obstacles: [
      // Boundary trees
      { id: 'tree1', x: 0, y: 0, width: 32, height: 32, type: 'obstacle', obstacleType: 'tree' },
      { id: 'tree2', x: 32, y: 0, width: 32, height: 32, type: 'obstacle', obstacleType: 'tree' },
      { id: 'tree3', x: 64, y: 0, width: 32, height: 32, type: 'obstacle', obstacleType: 'tree' },
      { id: 'tree4', x: 0, y: 32, width: 32, height: 32, type: 'obstacle', obstacleType: 'tree' },
      // Interior trees for paths
      { id: 'tree5', x: 200, y: 150, width: 32, height: 32, type: 'obstacle', obstacleType: 'tree' },
      { id: 'tree6', x: 400, y: 200, width: 32, height: 32, type: 'obstacle', obstacleType: 'tree' },
      { id: 'rock1', x: 300, y: 300, width: 32, height: 32, type: 'obstacle', obstacleType: 'rock' },
      { id: 'rock2', x: 500, y: 100, width: 32, height: 32, type: 'obstacle', obstacleType: 'rock' },
    ],
    enemies: [
      { id: 'slime1', x: 200, y: 200, width: 24, height: 24, type: 'enemy', health: 2, direction: 'down', speed: 1, lastDirectionChange: 0, aggressive: false, attackCooldown: 0 },
      { id: 'goblin1', x: 400, y: 300, width: 24, height: 24, type: 'enemy', health: 3, direction: 'left', speed: 1.5, lastDirectionChange: 0, aggressive: true, attackCooldown: 0 },
    ],
    collectibles: [
      { id: 'ruby1', x: 150, y: 250, width: 16, height: 16, type: 'collectible', collectibleType: 'ruby', value: 1, collected: false },
      { id: 'ruby2', x: 450, y: 150, width: 16, height: 16, type: 'collectible', collectibleType: 'ruby', value: 1, collected: false },
      { id: 'fairy1', x: 350, y: 100, width: 16, height: 16, type: 'collectible', collectibleType: 'fairy', value: 1, collected: false },
    ],
    npcs: [
      { id: 'sage1', x: 100, y: 100, width: 24, height: 24, type: 'npc', dialogue: ["Welcome, brave hero!", "The princess has been captured by the Shadow King!", "Collect rubies and fairies to grow stronger.", "Find the golden key to unlock the castle!"], currentDialogue: 0, hasSpoken: false },
    ]
  });

  // Screen 1: Dense forest with puzzles
  screens.push({
    obstacles: [
      { id: 'block1', x: 200, y: 200, width: 32, height: 32, type: 'obstacle', obstacleType: 'block', canPush: true },
      { id: 'block2', x: 300, y: 200, width: 32, height: 32, type: 'obstacle', obstacleType: 'block', canPush: true },
      { id: 'wall1', x: 150, y: 250, width: 32, height: 32, type: 'obstacle', obstacleType: 'wall' },
      { id: 'wall2', x: 350, y: 250, width: 32, height: 32, type: 'obstacle', obstacleType: 'wall' },
      { id: 'door1', x: 250, y: 400, width: 32, height: 32, type: 'obstacle', obstacleType: 'door', needsKey: true, isOpen: false },
    ],
    enemies: [
      { id: 'orc1', x: 150, y: 150, width: 28, height: 28, type: 'enemy', health: 4, direction: 'right', speed: 2, lastDirectionChange: 0, aggressive: true, attackCooldown: 0 },
      { id: 'slime2', x: 400, y: 350, width: 24, height: 24, type: 'enemy', health: 2, direction: 'up', speed: 1, lastDirectionChange: 0, aggressive: false, attackCooldown: 0 },
    ],
    collectibles: [
      { id: 'key1', x: 250, y: 300, width: 16, height: 16, type: 'collectible', collectibleType: 'key', value: 1, collected: false },
      { id: 'heart1', x: 400, y: 100, width: 16, height: 16, type: 'collectible', collectibleType: 'heart', value: 1, collected: false },
    ],
    npcs: []
  });

  // Screen 2: Castle area - final boss
  screens.push({
    obstacles: [
      { id: 'castle_wall1', x: 100, y: 100, width: 32, height: 32, type: 'obstacle', obstacleType: 'wall' },
      { id: 'castle_wall2', x: 132, y: 100, width: 32, height: 32, type: 'obstacle', obstacleType: 'wall' },
      { id: 'castle_wall3', x: 164, y: 100, width: 32, height: 32, type: 'obstacle', obstacleType: 'wall' },
    ],
    enemies: [
      { id: 'boss1', x: 300, y: 200, width: 48, height: 48, type: 'enemy', health: 10, direction: 'down', speed: 1, lastDirectionChange: 0, aggressive: true, attackCooldown: 0 },
    ],
    collectibles: [],
    npcs: [
      { id: 'princess', x: 400, y: 150, width: 24, height: 24, type: 'npc', dialogue: ["Thank you for rescuing me!", "You are truly a hero!", "The kingdom is safe once again!"], currentDialogue: 0, hasSpoken: false },
    ]
  });

  return screens;
};

// Game systems
const MovementSystem = (entities: any) => {
  const player = entities.player;
  const obstacles = entities.obstacles;
  
  if (!entities.keysPressed) entities.keysPressed = {};
  
  const moveLeft = entities.keysPressed['ArrowLeft'] || entities.keysPressed['a'] || entities.keysPressed['A'];
  const moveRight = entities.keysPressed['ArrowRight'] || entities.keysPressed['d'] || entities.keysPressed['D'];
  const moveUp = entities.keysPressed['ArrowUp'] || entities.keysPressed['w'] || entities.keysPressed['W'];
  const moveDown = entities.keysPressed['ArrowDown'] || entities.keysPressed['s'] || entities.keysPressed['S'];
  const attack = entities.keysPressed[' '] || entities.keysPressed['Enter'];

  let newX = player.x;
  let newY = player.y;
  let moving = false;

  // Handle attack
  if (attack && player.attackCooldown <= 0) {
    player.isAttacking = true;
    player.attackCooldown = 30;
    entities.soundManager?.play('sword');
    
    // Create sword projectile
    const swordX = player.x + (player.direction === 'left' ? -32 : player.direction === 'right' ? 32 : 0);
    const swordY = player.y + (player.direction === 'up' ? -32 : player.direction === 'down' ? 32 : 0);
    
    entities.projectiles.push({
      id: `sword_${Date.now()}`,
      x: swordX,
      y: swordY,
      width: 24,
      height: 24,
      type: 'projectile',
      direction: player.direction,
      speed: 0, // Sword stays in place briefly
      damage: 1,
      lifetime: 10
    });
  }

  // Handle movement
  if (moveLeft) {
    newX -= PLAYER_SPEED;
    player.direction = 'left';
    moving = true;
  }
  if (moveRight) {
    newX += PLAYER_SPEED;
    player.direction = 'right';
    moving = true;
  }
  if (moveUp) {
    newY -= PLAYER_SPEED;
    player.direction = 'up';
    moving = true;
  }
  if (moveDown) {
    newY += PLAYER_SPEED;
    player.direction = 'down';
    moving = true;
  }

  player.isMoving = moving;

  // Collision detection with obstacles
  let canMove = true;
  const playerRect = { x: newX, y: newY, width: player.width, height: player.height };
  
  obstacles.forEach((obstacle: Obstacle) => {
    if (checkCollision(playerRect, obstacle)) {
      if (obstacle.obstacleType === 'door' && obstacle.needsKey && !obstacle.isOpen) {
        if (player.hasKey) {
          obstacle.isOpen = true;
          entities.soundManager?.play('puzzle');
        } else {
          canMove = false;
        }
      } else if (obstacle.obstacleType === 'block' && obstacle.canPush) {
        // Push block
        const pushX = obstacle.x + (player.direction === 'left' ? -TILE_SIZE : player.direction === 'right' ? TILE_SIZE : 0);
        const pushY = obstacle.y + (player.direction === 'up' ? -TILE_SIZE : player.direction === 'down' ? TILE_SIZE : 0);
        
        let canPush = true;
        obstacles.forEach((other: Obstacle) => {
          if (other.id !== obstacle.id && checkCollision({ x: pushX, y: pushY, width: TILE_SIZE, height: TILE_SIZE }, other)) {
            canPush = false;
          }
        });
        
        if (canPush && pushX >= 0 && pushY >= 0 && pushX < GAME_WIDTH - TILE_SIZE && pushY < GAME_HEIGHT - TILE_SIZE) {
          obstacle.x = pushX;
          obstacle.y = pushY;
          entities.soundManager?.play('puzzle');
        } else {
          canMove = false;
        }
      } else if (!obstacle.isOpen) {
        canMove = false;
      }
    }
  });

  // Boundary checks
  if (newX < 0 || newX > GAME_WIDTH - player.width || newY < 0 || newY > GAME_HEIGHT - player.height) {
    // Screen transition
    if (newX < 0 && entities.currentScreen > 0) {
      entities.currentScreen--;
      player.x = GAME_WIDTH - player.width - 10;
      entities.screenTransition = true;
    } else if (newX > GAME_WIDTH - player.width && entities.currentScreen < entities.worldData.length - 1) {
      entities.currentScreen++;
      player.x = 10;
      entities.screenTransition = true;
    } else if (newY < 0 && entities.currentScreen > 0) {
      entities.currentScreen--;
      player.y = GAME_HEIGHT - player.height - 10;
      entities.screenTransition = true;
    } else if (newY > GAME_HEIGHT - player.height && entities.currentScreen < entities.worldData.length - 1) {
      entities.currentScreen++;
      player.y = 10;
      entities.screenTransition = true;
    } else {
      canMove = false;
    }
  }

  if (canMove && !entities.screenTransition) {
    player.x = newX;
    player.y = newY;
  }

  // Update cooldowns
  if (player.attackCooldown > 0) player.attackCooldown--;
  if (player.attackCooldown <= 0) player.isAttacking = false;
  if (player.invulnerable > 0) player.invulnerable--;

  return entities;
};

const EnemySystem = (entities: any) => {
  const player = entities.player;
  const enemies = entities.enemies;
  const obstacles = entities.obstacles;

  enemies.forEach((enemy: Enemy, index: number) => {
    // Simple AI
    const now = Date.now();
    
    // Change direction randomly
    if (now - enemy.lastDirectionChange > 2000 + Math.random() * 3000) {
      const directions: ('up' | 'down' | 'left' | 'right')[] = ['up', 'down', 'left', 'right'];
      enemy.direction = directions[Math.floor(Math.random() * directions.length)];
      enemy.lastDirectionChange = now;
    }
    
    // Move towards player if aggressive and close
    if (enemy.aggressive) {
      const distanceToPlayer = Math.sqrt(Math.pow(player.x - enemy.x, 2) + Math.pow(player.y - enemy.y, 2));
      if (distanceToPlayer < 100) {
        if (Math.abs(player.x - enemy.x) > Math.abs(player.y - enemy.y)) {
          enemy.direction = player.x > enemy.x ? 'right' : 'left';
        } else {
          enemy.direction = player.y > enemy.y ? 'down' : 'up';
        }
      }
    }
    
    // Move enemy
    let newX = enemy.x;
    let newY = enemy.y;
    
    switch (enemy.direction) {
      case 'left': newX -= enemy.speed; break;
      case 'right': newX += enemy.speed; break;
      case 'up': newY -= enemy.speed; break;
      case 'down': newY += enemy.speed; break;
    }
    
    // Check collision with obstacles
    let canMove = true;
    const enemyRect = { x: newX, y: newY, width: enemy.width, height: enemy.height };
    
    obstacles.forEach((obstacle: Obstacle) => {
      if (checkCollision(enemyRect, obstacle)) {
        canMove = false;
      }
    });
    
    // Boundary checks
    if (newX < 0 || newX > GAME_WIDTH - enemy.width || newY < 0 || newY > GAME_HEIGHT - enemy.height) {
      canMove = false;
    }
    
    if (canMove) {
      enemy.x = newX;
      enemy.y = newY;
    } else {
      // Turn around if blocked
      const directions: ('up' | 'down' | 'left' | 'right')[] = ['up', 'down', 'left', 'right'];
      enemy.direction = directions[Math.floor(Math.random() * directions.length)];
    }
    
    // Attack player if close
    if (enemy.attackCooldown <= 0) {
      const distanceToPlayer = Math.sqrt(Math.pow(player.x - enemy.x, 2) + Math.pow(player.y - enemy.y, 2));
      if (distanceToPlayer < 30 && player.invulnerable <= 0) {
        player.health--;
        player.invulnerable = 60; // 1 second of invulnerability
        entities.soundManager?.play('hurt');
        
        if (player.health <= 0) {
          entities.gameOver = true;
        }
      }
      enemy.attackCooldown = 60; // 1 second attack cooldown
    }
    
    if (enemy.attackCooldown > 0) enemy.attackCooldown--;
  });

  return entities;
};

const ProjectileSystem = (entities: any) => {
  const projectiles = entities.projectiles;
  const enemies = entities.enemies;
  
  projectiles.forEach((projectile: Projectile, index: number) => {
    projectile.lifetime--;
    
    if (projectile.lifetime <= 0) {
      projectiles.splice(index, 1);
      return;
    }
    
    // Check collision with enemies
    enemies.forEach((enemy: Enemy, enemyIndex: number) => {
      if (checkCollision(projectile, enemy)) {
        enemy.health -= projectile.damage;
        entities.soundManager?.play('enemy_hit');
        projectiles.splice(index, 1);
        
        if (enemy.health <= 0) {
          enemies.splice(enemyIndex, 1);
          // Drop ruby
          entities.collectibles.push({
            id: `ruby_drop_${Date.now()}`,
            x: enemy.x,
            y: enemy.y,
            width: 16,
            height: 16,
            type: 'collectible',
            collectibleType: 'ruby',
            value: 1,
            collected: false
          });
        }
      }
    });
  });

  return entities;
};

const CollectibleSystem = (entities: any) => {
  const player = entities.player;
  const collectibles = entities.collectibles;
  
  collectibles.forEach((collectible: Collectible, index: number) => {
    if (!collectible.collected && checkCollision(player, collectible)) {
      collectible.collected = true;
      entities.soundManager?.play('collect');
      
      switch (collectible.collectibleType) {
        case 'ruby':
          player.rubies += collectible.value;
          break;
        case 'fairy':
          player.fairies += collectible.value;
          break;
        case 'heart':
          player.health = Math.min(player.health + collectible.value, player.maxHealth);
          break;
        case 'key':
          player.hasKey = true;
          break;
      }
      
      collectibles.splice(index, 1);
    }
  });

  return entities;
};

const ScreenSystem = (entities: any) => {
  if (entities.screenTransition) {
    const currentScreenData = entities.worldData[entities.currentScreen];
    
    // Update entities for new screen
    entities.obstacles = currentScreenData.obstacles;
    entities.enemies = currentScreenData.enemies.map((e: any) => ({ ...e, lastDirectionChange: Date.now() }));
    entities.collectibles = currentScreenData.collectibles;
    entities.npcs = currentScreenData.npcs;
    
    entities.screenTransition = false;
  }
  
  // Check win condition
  if (entities.currentScreen === 2 && entities.enemies.length === 0) {
    entities.gameWon = true;
  }
  
  return entities;
};

const DialogueSystem = (entities: any) => {
  const player = entities.player;
  const npcs = entities.npcs;
  const interact = entities.keysPressed['e'] || entities.keysPressed['E'];
  
  if (interact) {
    npcs.forEach((npc: NPC) => {
      const distance = Math.sqrt(Math.pow(player.x - npc.x, 2) + Math.pow(player.y - npc.y, 2));
      if (distance < 50) {
        if (!entities.showDialogue) {
          entities.showDialogue = true;
          entities.currentDialogue = npc.dialogue;
          entities.dialogueIndex = 0;
          npc.hasSpoken = true;
        } else {
          entities.dialogueIndex++;
          if (entities.dialogueIndex >= entities.currentDialogue.length) {
            entities.showDialogue = false;
            entities.currentDialogue = [];
            entities.dialogueIndex = 0;
          }
        }
      }
    });
  }
  
  return entities;
};

// Utility functions
const checkCollision = (rect1: any, rect2: any) => {
  return rect1.x < rect2.x + rect2.width &&
         rect1.x + rect1.width > rect2.x &&
         rect1.y < rect2.y + rect2.height &&
         rect1.y + rect1.height > rect2.y;
};

// Renderer
const GameRenderer = (entities: any, screen: any) => {
  if (!entities || !entities.player) {
    return <div style={{ width: GAME_WIDTH, height: GAME_HEIGHT, background: '#2d5016' }} />;
  }
  
  const player = entities.player;
  
  return (
    <div style={{ 
      width: GAME_WIDTH, 
      height: GAME_HEIGHT, 
      position: 'relative', 
      overflow: 'hidden',
      background: 'linear-gradient(to bottom, #4a7c59 0%, #2d5016 50%, #1a3009 100%)' // Forest background
    }}>
      {/* Obstacles */}
      {entities.obstacles?.map((obstacle: Obstacle) => (
        <div
          key={obstacle.id}
          style={{
            position: 'absolute',
            left: obstacle.x,
            top: obstacle.y,
            width: obstacle.width,
            height: obstacle.height,
            backgroundColor: getObstacleColor(obstacle.obstacleType, obstacle.isOpen),
            border: '1px solid #000',
            borderRadius: obstacle.obstacleType === 'rock' ? '50%' : '2px'
          }}
        />
      ))}
      
      {/* Collectibles */}
      {entities.collectibles?.map((collectible: Collectible) => 
        !collectible.collected && (
          <div
            key={collectible.id}
            style={{
              position: 'absolute',
              left: collectible.x,
              top: collectible.y,
              width: collectible.width,
              height: collectible.height,
              backgroundColor: getCollectibleColor(collectible.collectibleType),
              border: '1px solid #fff',
              borderRadius: '50%',
              boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
              animation: 'pulse 2s infinite'
            }}
          />
        )
      )}
      
      {/* Enemies */}
      {entities.enemies?.map((enemy: Enemy) => (
        <div
          key={enemy.id}
          style={{
            position: 'absolute',
            left: enemy.x,
            top: enemy.y,
            width: enemy.width,
            height: enemy.height,
            backgroundColor: getEnemyColor(enemy.id),
            border: '2px solid #000',
            borderRadius: '4px'
          }}
        />
      ))}
      
      {/* NPCs */}
      {entities.npcs?.map((npc: NPC) => (
        <div
          key={npc.id}
          style={{
            position: 'absolute',
            left: npc.x,
            top: npc.y,
            width: npc.width,
            height: npc.height,
            backgroundColor: npc.id.includes('princess') ? '#ff69b4' : '#8a2be2',
            border: '2px solid #fff',
            borderRadius: '4px'
          }}
        />
      ))}
      
      {/* Projectiles */}
      {entities.projectiles?.map((projectile: Projectile) => (
        <div
          key={projectile.id}
          style={{
            position: 'absolute',
            left: projectile.x,
            top: projectile.y,
            width: projectile.width,
            height: projectile.height,
            backgroundColor: '#c0c0c0',
            border: '1px solid #000',
            borderRadius: '2px'
          }}
        />
      ))}
      
      {/* Player */}
      <div
        style={{
          position: 'absolute',
          left: player.x,
          top: player.y,
          width: player.width,
          height: player.height,
          backgroundColor: player.invulnerable > 0 ? '#ff6b6b' : '#4dabf7',
          border: '2px solid #1864ab',
          borderRadius: '4px',
          opacity: player.invulnerable > 0 ? 0.7 : 1
        }}
      />
      
      {/* UI */}
      <div style={{ position: 'absolute', top: 10, left: 10, color: 'white', fontFamily: 'monospace', fontSize: '16px', textShadow: '2px 2px 4px black' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Heart size={16} color="#ff6b6b" />
            <span>{player.health}/{player.maxHealth}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Gem size={16} color="#ff0080" />
            <span>{player.rubies}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ color: '#00ff80' }}>🧚</span>
            <span>{player.fairies}</span>
          </div>
          {player.hasKey && <span style={{ color: '#ffd700' }}>🗝️</span>}
        </div>
        <div>Screen: {entities.currentScreen + 1}/{entities.worldData?.length || 1}</div>
      </div>
      
      {/* Dialogue */}
      {entities.showDialogue && entities.currentDialogue && (
        <div style={{
          position: 'absolute',
          bottom: 20,
          left: 20,
          right: 20,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '15px',
          borderRadius: '8px',
          border: '2px solid #fff',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          {entities.currentDialogue[entities.dialogueIndex]}
          <div style={{ fontSize: '12px', marginTop: '10px', opacity: 0.7 }}>
            Press E to continue...
          </div>
        </div>
      )}
    </div>
  );
};

// Helper functions
const getObstacleColor = (type: string, isOpen?: boolean) => {
  switch (type) {
    case 'tree': return '#0d5016';
    case 'rock': return '#666';
    case 'water': return '#4dabf7';
    case 'wall': return '#8b4513';
    case 'door': return isOpen ? '#90ee90' : '#8b4513';
    case 'block': return '#654321';
    default: return '#333';
  }
};

const getCollectibleColor = (type: string) => {
  switch (type) {
    case 'ruby': return '#ff0080';
    case 'fairy': return '#00ff80';
    case 'heart': return '#ff6b6b';
    case 'key': return '#ffd700';
    default: return '#fff';
  }
};

const getEnemyColor = (id: string) => {
  if (id.includes('slime')) return '#90ee90';
  if (id.includes('goblin')) return '#8b4513';
  if (id.includes('orc')) return '#654321';
  if (id.includes('boss')) return '#8b0000';
  return '#ff6b6b';
};

interface ZeldaRPGProps {
  onClose: () => void;
}

export const ZeldaRPG: React.FC<ZeldaRPGProps> = ({ onClose }) => {
  const [gameState, setGameState] = useState<'menu' | 'playing' | 'gameOver' | 'gameWon'>('menu');
  const [gameKey, setGameKey] = useState(0);
  const soundManagerRef = useRef<SoundManager>(new SoundManager());
  const [soundEnabled, setSoundEnabled] = useState(true);
  const keysRef = useRef<{ [key: string]: boolean }>({});

  const resetGame = () => {
    keysRef.current = {};
    setGameKey(prev => prev + 1);
    setGameState('menu');
  };

  const toggleSound = () => {
    const enabled = soundManagerRef.current.toggle();
    setSoundEnabled(enabled);
  };

  const worldData = generateWorld();
  
  const initialEntities = {
    player: {
      id: 'player',
      x: 100,
      y: 300,
      width: 24,
      height: 24,
      type: 'player',
      health: 6,
      maxHealth: 6,
      direction: 'down' as const,
      isMoving: false,
      isAttacking: false,
      attackCooldown: 0,
      invulnerable: 0,
      rubies: 0,
      fairies: 0,
      hasKey: false,
      currentScreen: 0
    },
    enemies: worldData[0].enemies,
    collectibles: worldData[0].collectibles,
    obstacles: worldData[0].obstacles,
    projectiles: [],
    npcs: worldData[0].npcs,
    camera: { x: 0, y: 0 },
    keysPressed: keysRef.current,
    gameStarted: false,
    gameWon: false,
    gameOver: false,
    currentScreen: 0,
    showDialogue: false,
    currentDialogue: [],
    dialogueIndex: 0,
    screenTransition: false,
    worldData: worldData,
    soundManager: soundManagerRef.current
  };

  // Keyboard event handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (gameState !== 'playing') return;
      keysRef.current[e.key] = true;
      e.preventDefault();
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (gameState !== 'playing') return;
      keysRef.current[e.key] = false;
      e.preventDefault();
    };

    if (gameState !== 'playing') {
      keysRef.current = {};
    }

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState]);

  // Game Won Screen
  if (gameState === 'gameWon') {
    return (
      <div className="w-full h-full bg-gradient-to-b from-yellow-400 to-yellow-600 flex flex-col items-center justify-center p-8">
        <div className="bg-white/90 rounded-xl p-8 max-w-md text-center shadow-2xl">
          <h1 className="text-4xl font-bold text-yellow-600 mb-4">🏆 Victory!</h1>
          <p className="text-gray-700 mb-4">
            Congratulations! You have rescued the princess and saved the kingdom!
          </p>
          <div className="mb-6">
            <p className="text-lg font-semibold text-gray-800">Quest Complete!</p>
            <p className="text-sm text-gray-600">You are a true hero!</p>
          </div>
          
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={() => setGameState('menu')}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-6 py-2"
            >
              Play Again
            </Button>
            <Button variant="outline" onClick={onClose}>
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Game Over Screen
  if (gameState === 'gameOver') {
    return (
      <div className="w-full h-full bg-gradient-to-b from-red-400 to-red-600 flex flex-col items-center justify-center p-8">
        <div className="bg-white/90 rounded-xl p-8 max-w-md text-center shadow-2xl">
          <h1 className="text-4xl font-bold text-red-600 mb-4">💀 Game Over!</h1>
          <p className="text-gray-700 mb-4">
            The hero has fallen! The princess remains captive...
          </p>
          <div className="mb-6">
            <p className="text-lg font-semibold text-gray-800">Try again, brave warrior!</p>
          </div>
          
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={() => setGameState('menu')}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2"
            >
              Try Again
            </Button>
            <Button variant="outline" onClick={onClose}>
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (gameState === 'menu') {
    return (
      <div className="w-full h-full bg-gradient-to-b from-green-400 to-green-600 flex flex-col items-center justify-center p-8">
        <div className="bg-white/90 rounded-xl p-8 max-w-lg text-center shadow-2xl">
          <h1 className="text-4xl font-bold text-green-600 mb-4">🗡️ MapleOS Quest</h1>
          <p className="text-gray-700 mb-4">
            A classic top-down RPG adventure! Explore mystical forests, battle fearsome enemies, 
            solve ancient puzzles, and rescue the princess from the Shadow King!
          </p>
          
          <div className="mb-6 text-left text-sm text-gray-600 space-y-2">
            <h3 className="font-semibold text-gray-800">Controls:</h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2">
                <ArrowUp size={16} />
                <ArrowDown size={16} />
                <ArrowLeft size={16} />
                <ArrowRight size={16} />
                <span>Move (WASD)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono bg-gray-200 px-2 py-1 rounded">SPACE</span>
                <span>Attack</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono bg-gray-200 px-2 py-1 rounded">E</span>
                <span>Interact/Talk</span>
              </div>
            </div>
            <div className="mt-4 space-y-1">
              <p><span className="text-red-500">❤️</span> Health - Don't let it reach zero!</p>
              <p><span className="text-pink-500">💎</span> Rubies - Collect for points</p>
              <p><span className="text-green-500">🧚</span> Fairies - Magical helpers</p>
              <p><span className="text-yellow-500">🗝️</span> Keys - Unlock doors and secrets</p>
            </div>
          </div>
          
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={() => setGameState('playing')}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-2"
            >
              Begin Quest
            </Button>
            <Button variant="outline" onClick={onClose}>
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-background flex flex-col items-center justify-center p-4">
      <div className="mb-4 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleSound}
          className="flex items-center gap-2"
        >
          {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          {soundEnabled ? 'Sound On' : 'Sound Off'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={resetGame}
          className="flex items-center gap-2"
        >
          <RotateCcw size={16} />
          Reset
        </Button>
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
      
      <div className="border-4 border-gray-800 rounded-lg overflow-hidden shadow-2xl">
        <GameEngine
          key={gameKey}
          systems={[MovementSystem, EnemySystem, ProjectileSystem, CollectibleSystem, ScreenSystem, DialogueSystem]}
          entities={initialEntities}
          renderer={GameRenderer}
          running={gameState === 'playing'}
          onEvent={(entities) => {
            if (entities.gameOver) {
              setGameState('gameOver');
            }
            if (entities.gameWon) {
              setGameState('gameWon');
            }
          }}
          style={{ width: GAME_WIDTH, height: GAME_HEIGHT }}
        />
      </div>
      
      <div className="mt-4 text-center text-sm text-muted-foreground max-w-2xl">
        <p>Explore the mystical forest, defeat enemies with your sword, collect treasures, and save the princess! Navigate between screens to discover new areas and challenges.</p>
      </div>
    </div>
  );
};